import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import * as apiWarehouse from 'api/apiWarehouses';
import { useAuth } from 'context/AuthContext';
import { useGlobal } from 'context/GlobalContext';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import type { StockRow } from '@econtractor/types';
import vconst from 'utils/helpers/structs/vconst';
import { useWarehouse } from '../context/WarehouseProvider';
import { exportStocksToExcel, type ExportStockLot } from './exportStocksToExcel';

const LOTS_CHUNK = 250;

type LotsResponseItem = {
    productId?: number;
    lots?: Array<{
        operPrice?: number | null;
        operPriceRate?: number | null;
        stock?: number;
    }>;
};

const loadStockLots = async (
    warehouseId: number,
    productIds: number[],
): Promise<Record<number, ExportStockLot[]> | null> => {
    const byProduct: Record<number, ExportStockLot[]> = {};
    for (const id of productIds) byProduct[id] = [];

    for (let offset = 0; offset < productIds.length; offset += LOTS_CHUNK) {
        const chunk = productIds.slice(offset, offset + LOTS_CHUNK);
        const res = await apiWarehouse.getStockLots(warehouseId, chunk);
        if (!Array.isArray(res)) return null;

        for (const item of res as LotsResponseItem[]) {
            const productId = Number(item?.productId || 0);
            if (!productId) continue;
            const lots = Array.isArray(item?.lots) ? item.lots : [];
            byProduct[productId] = lots.map((lot) => ({
                operPrice: lot?.operPrice == null ? null : Number(lot.operPrice),
                operPriceRate: lot?.operPriceRate == null ? null : Number(lot.operPriceRate),
                stock: Number(lot?.stock || 0),
            }));
        }
    }

    return byProduct;
};

export const useExportStocks = (wId: number) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const { showToast } = useToast();
    const { g } = useGlobal();
    const { auth } = useAuth();
    const { localSettings } = useSettings();
    const location = useLocation();
    const {
        stockFilter,
        stocksOrder,
        stocksDesc,
        showCategory,
        showArticle,
        showUpdated,
        showCredit,
        showLotBreakdown,
    } = useWarehouse();
    const [exporting, setExporting] = useState(false);

    const exportStocks = useCallback(async () => {
        if (!wId || exporting) return;

        setExporting(true);
        try {
            const search = `%${g?.search?.[location.pathname] || ''}%`;
            const resStock = await apiWarehouse.getStocks(wId, {
                search,
                page: 1,
                onPage: 999999,
                order: `${stocksOrder ? `order by ${stocksOrder} ` : ''} ${stocksDesc ? 'DESC' : ''}`,
                ...stockFilter,
            });

            if (!resStock) {
                showToast(t('common:messError'), 'danger');
                return;
            }

            const stocks = (resStock.stocks || []) as StockRow[];
            if (!stocks.length) {
                showToast(t('messExportEmpty'), 'warning');
                return;
            }

            const isUahMode = (Number(localSettings?.RATE?.current ?? 1) || 1) === 1;
            const showBalance = Boolean(
                showCredit
                && auth?.user?.access?.role === 'manager'
                && wId === vconst.waherouseMainId,
            );
            const showLots = showLotBreakdown;
            const showBothCurrencies = showLots && !isUahMode;

            let lotsByProduct: Record<number, ExportStockLot[]> = {};
            if (showLots) {
                const productIds = stocks
                    .map((row) => Number(row.productId || 0))
                    .filter((id) => id > 0);
                const loaded = await loadStockLots(wId, productIds);
                if (!loaded) {
                    showToast(t('common:messError'), 'danger');
                    return;
                }
                lotsByProduct = loaded;
            }

            await exportStocksToExcel(stocks, wId, {
                prodName: t('fields.prodName'),
                category: t('fields.category'),
                article: t('fields.article'),
                balance: t('fields.balance'),
                countEnd: t('fields.countEnd'),
                updated: t('fields.updated'),
                price: t('labelPrice'),
                rate: t('labelRate'),
            }, {
                showCategory,
                showArticle,
                showBalance,
                showUpdated,
                showLots,
                showBothCurrencies,
            }, lotsByProduct);
        } catch {
            showToast(t('common:messError'), 'danger');
        } finally {
            setExporting(false);
        }
    }, [
        wId,
        exporting,
        g?.search,
        location.pathname,
        stocksOrder,
        stocksDesc,
        stockFilter,
        showCategory,
        showArticle,
        showUpdated,
        showCredit,
        showLotBreakdown,
        localSettings?.RATE?.current,
        auth?.user?.access?.role,
        showToast,
        t,
    ]);

    return { exportStocks, exporting };
};
